import { BadgeAttachmentPoint, BadgeSide } from './badgeGeometry'

export interface RoutePoint { x: number; y: number }
export interface RoundedCorner { point: RoutePoint; radius: number }

export const CONNECTION_LEAD_OUT = 18

function pixelPoint(point: RoutePoint): RoutePoint {
    return { x: Math.round(point.x), y: Math.round(point.y) }
}

export function leadOutPoint(point: BadgeAttachmentPoint, side: BadgeSide, distance = CONNECTION_LEAD_OUT): RoutePoint {
    const result = pixelPoint(point)
    if (side === 'left') result.x -= distance
    if (side === 'right') result.x += distance
    if (side === 'top') result.y -= distance
    if (side === 'bottom') result.y += distance
    return pixelPoint(result)
}

function appendPoint(points: RoutePoint[], point: RoutePoint): void {
    const next = pixelPoint(point)
    const last = points[points.length - 1]
    if (!last || last.x !== next.x || last.y !== next.y) points.push(next)
}

function simplify(points: RoutePoint[]): RoutePoint[] {
    return points.filter((point, index) => {
        if (index === 0 || index === points.length - 1) return true
        const previous = points[index - 1]
        const next = points[index + 1]
        return !((previous.x === point.x && point.x === next.x) || (previous.y === point.y && point.y === next.y))
    })
}

export function routeOrthogonalConnection(
    from: BadgeAttachmentPoint,
    fromSide: BadgeSide,
    to: BadgeAttachmentPoint,
    toSide: BadgeSide,
    leadOut = CONNECTION_LEAD_OUT,
): RoutePoint[] {
    const start = pixelPoint(from)
    const end = pixelPoint(to)
    const fromLead = leadOutPoint(start, fromSide, leadOut)
    const toLead = leadOutPoint(end, toSide, leadOut)
    const points: RoutePoint[] = []
    appendPoint(points, start)
    appendPoint(points, fromLead)

    const fromHorizontal = fromSide === 'left' || fromSide === 'right'
    const toHorizontal = toSide === 'left' || toSide === 'right'
    if (fromHorizontal !== toHorizontal) {
        appendPoint(points, fromHorizontal
            ? { x: toLead.x, y: fromLead.y }
            : { x: fromLead.x, y: toLead.y })
    } else if (fromHorizontal) {
        const middleX = Math.round((fromLead.x + toLead.x) / 2)
        appendPoint(points, { x: middleX, y: fromLead.y })
        appendPoint(points, { x: middleX, y: toLead.y })
    } else {
        const middleY = Math.round((fromLead.y + toLead.y) / 2)
        appendPoint(points, { x: fromLead.x, y: middleY })
        appendPoint(points, { x: toLead.x, y: middleY })
    }
    appendPoint(points, toLead)
    appendPoint(points, end)
    return simplify(points)
}

export function boundedCornerRadius(points: RoutePoint[], index: number, requestedRadius: number): number {
    if (index <= 0 || index >= points.length - 1) return 0
    const previous = points[index - 1]
    const point = points[index]
    const next = points[index + 1]
    const incoming = Math.abs(point.x - previous.x) + Math.abs(point.y - previous.y)
    const outgoing = Math.abs(next.x - point.x) + Math.abs(next.y - point.y)
    const turns = (previous.x === point.x) !== (point.x === next.x)
    return turns ? Math.max(0, Math.min(Math.round(requestedRadius), Math.floor(incoming / 2), Math.floor(outgoing / 2))) : 0
}

export function roundedCorners(points: RoutePoint[], requestedRadius: number): RoundedCorner[] {
    return points.slice(1, -1).map((point, offset) => ({
        point,
        radius: boundedCornerRadius(points, offset + 1, requestedRadius),
    }))
}

export function isOrthogonalRoute(points: RoutePoint[]): boolean {
    return points.slice(1).every((point, index) => point.x === points[index].x || point.y === points[index].y)
}
